// src/components/feedback/FeedbackPanelHost.tsx
//
// フィードバック入力パネル。App 直下に 1 つだけ常駐させる。
// - 入力内容と添付画像は、パネルを閉じても、画面を遷移しても保持する（送信成功時だけ消す）
// - 開いたままでも画面を操作できるよう、外側クリックや Esc では閉じない
import React from 'react';
import { useLocation } from 'react-router-dom';
import { t as uiText } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import { FEEDBACK_WIDGET_ATTR, captureDomScreenshot } from '../../features/feedback/capture';
import { setFeedbackPanelOpen, useFeedbackPanelOpen } from '../../features/feedback/feedbackPanelStore';
import { FeedbackValidationError, submitFeedback } from '../../features/feedback/submit';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_LIMITS,
  categoryNeedsExpectedActual,
  type FeedbackCategory,
  type FeedbackFile,
} from '../../features/feedback/types';
import { feedbackCategoryLabel } from '../../features/feedback/labels';
import './FeedbackWidget.css';

const SENT_MESSAGE_MS = 1200;

function newFileId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const FeedbackPanelHost: React.FC = () => {
  const { user } = useAuth();
  const { workspaces } = useWorkspaceContext();
  const location = useLocation();
  const open = useFeedbackPanelOpen();

  const [category, setCategory] = React.useState<FeedbackCategory>('feature');
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [expected, setExpected] = React.useState('');
  const [actual, setActual] = React.useState('');
  const [files, setFiles] = React.useState<FeedbackFile[]>([]);
  const [capturing, setCapturing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const previewFile = files.find((file) => file.id === previewId) ?? null;

  // 拡大表示は Esc で閉じる
  React.useEffect(() => {
    if (!previewFile) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewFile]);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const filesRef = React.useRef<FeedbackFile[]>([]);
  filesRef.current = files;

  const busy = capturing || submitting || sent;
  const withDetails = categoryNeedsExpectedActual(category);

  // 表示中のワークスペース（URL の先頭が自分のワークスペース ID のときだけ）
  const workspaceId = React.useMemo(() => {
    const first = location.pathname.split('/')[1] ?? '';
    return workspaces.some((workspace) => workspace.id === first) ? first : null;
  }, [location.pathname, workspaces]);

  const resetDraft = React.useCallback(() => {
    setPreviewId(null);
    filesRef.current.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    filesRef.current = [];
    setCategory('feature');
    setTitle('');
    setBody('');
    setExpected('');
    setActual('');
    setFiles([]);
    setSent(false);
    setError(null);
  }, []);

  // 送信成功のメッセージを少し見せてから、閉じて下書きを消す
  React.useEffect(() => {
    if (!sent) return;
    const timer = window.setTimeout(() => {
      setFeedbackPanelOpen(false);
      resetDraft();
    }, SENT_MESSAGE_MS);
    return () => window.clearTimeout(timer);
  }, [sent, resetDraft]);

  React.useEffect(() => {
    return () => {
      filesRef.current.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    };
  }, []);

  const addImages = (blobs: Array<{ blob: Blob; filename: string }>) => {
    const room = FEEDBACK_LIMITS.attachmentsMax - filesRef.current.length;
    const images = blobs.filter(({ blob }) => blob.type.startsWith('image/'));
    if (images.length < blobs.length) {
      setError(uiText("Only image files can be attached."));
    }
    const tooLarge = images.filter(({ blob }) => blob.size > FEEDBACK_LIMITS.imageBytesMax);
    if (tooLarge.length > 0) {
      setError(uiText("Images must be 5MB or smaller."));
    }
    const accepted = images.filter(({ blob }) => blob.size <= FEEDBACK_LIMITS.imageBytesMax);
    if (accepted.length > room) {
      setError(uiText("You can attach up to {{max}} images.", { max: FEEDBACK_LIMITS.attachmentsMax }));
    }
    const added = accepted.slice(0, Math.max(0, room)).map(({ blob, filename }) => ({
      id: newFileId(),
      blob,
      filename,
      previewUrl: URL.createObjectURL(blob),
    }));
    if (added.length === 0) return;
    const next = [...filesRef.current, ...added];
    filesRef.current = next;
    setFiles(next);
  };

  const removeFile = (id: string) => {
    const target = filesRef.current.find((file) => file.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    const next = filesRef.current.filter((file) => file.id !== id);
    filesRef.current = next;
    setFiles(next);
  };

  const runCapture = async () => {
    if (filesRef.current.length >= FEEDBACK_LIMITS.attachmentsMax) {
      setError(uiText("You can attach up to {{max}} images.", { max: FEEDBACK_LIMITS.attachmentsMax }));
      return;
    }
    setError(null);
    setCapturing(true);
    try {
      const blob = await captureDomScreenshot();
      addImages([{ blob, filename: `screenshot-${Date.now()}.png` }]);
    } catch (captureError) {
      console.warn('[Feedback] capture failed', captureError);
      setError(uiText("Could not capture the screen."));
    } finally {
      setCapturing(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const item = Array.from(event.clipboardData?.items ?? []).find((entry) =>
      entry.type.startsWith('image/')
    );
    const file = item?.getAsFile();
    if (!file) return;
    event.preventDefault();
    addImages([{ blob: file, filename: file.name || `pasted-${Date.now()}.png` }]);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setSubmitting(true);
    try {
      await submitFeedback({ category, title, body, expected, actual, files, workspaceId });
      setSent(true);
    } catch (submitError) {
      console.error('[Feedback] submit failed', submitError);
      setError(
        submitError instanceof FeedbackValidationError
          ? submitError.message
          : uiText("Could not send feedback. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ログアウト中は出さない（状態は保持したまま）
  if (!user || !open) return null;

  return (
    <>
    <form
      className={`feedback-panel${capturing ? ' feedback-widget--capturing' : ''}`}
      role="dialog"
      aria-label={uiText("Send feedback")}
      onSubmit={handleSubmit}
      onPaste={handlePaste}
      {...{ [FEEDBACK_WIDGET_ATTR]: '' }}
    >
      <div className="feedback-panel__header">
        <div className="feedback-panel__title">{uiText("Send feedback")}</div>
        <button
          type="button"
          className="feedback-panel__close"
          aria-label={uiText("Close")}
          onClick={() => setFeedbackPanelOpen(false)}
          disabled={capturing || submitting}
        >
          ×
        </button>
      </div>

      {sent ? (
        <div className="feedback-panel__sent">{uiText("Thank you. Your feedback was sent.")}</div>
      ) : (
        <div className="feedback-panel__body">
          <label className="feedback-panel__field">
            <span className="feedback-panel__label">{uiText("Type")}</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value as FeedbackCategory)}
              disabled={busy}
            >
              {FEEDBACK_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {feedbackCategoryLabel(value)}
                </option>
              ))}
            </select>
          </label>

          <label className="feedback-panel__field">
            <span className="feedback-panel__label">{uiText("Subject")}</span>
            <input
              type="text"
              value={title}
              maxLength={FEEDBACK_LIMITS.titleMax}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={uiText("Summarize in one line")}
              disabled={busy}
              required
            />
          </label>

          <label className="feedback-panel__field">
            <span className="feedback-panel__label">{uiText("Details")}</span>
            <textarea
              value={body}
              maxLength={FEEDBACK_LIMITS.bodyMax}
              onChange={(event) => setBody(event.target.value)}
              placeholder={uiText("What happened, or what would you like?")}
              rows={4}
              disabled={busy}
              required
            />
          </label>

          {withDetails && (
            <>
              <label className="feedback-panel__field">
                <span className="feedback-panel__label">{uiText("Expected behavior")}</span>
                <textarea
                  value={expected}
                  maxLength={FEEDBACK_LIMITS.expectedMax}
                  onChange={(event) => setExpected(event.target.value)}
                  rows={2}
                  disabled={busy}
                />
              </label>
              <label className="feedback-panel__field">
                <span className="feedback-panel__label">{uiText("Actual behavior")}</span>
                <textarea
                  value={actual}
                  maxLength={FEEDBACK_LIMITS.actualMax}
                  onChange={(event) => setActual(event.target.value)}
                  rows={2}
                  disabled={busy}
                />
              </label>
            </>
          )}

          <div className="feedback-panel__field">
            <span className="feedback-panel__label">
              {uiText("Images ({{count}}/{{max}})", {
                count: files.length,
                max: FEEDBACK_LIMITS.attachmentsMax,
              })}
            </span>
            <div className="feedback-panel__attach-actions">
              <button type="button" onClick={() => void runCapture()} disabled={busy}>
                {capturing ? uiText("Capturing...") : uiText("Capture this screen")}
              </button>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                {uiText("Choose images")}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                event.target.value = '';
                addImages(picked.map((file) => ({ blob: file, filename: file.name })));
              }}
            />
            <div className="feedback-panel__hint">{uiText("You can also paste an image here.")}</div>
            {files.length > 0 && (
              <ul className="feedback-panel__thumbs">
                {files.map((file) => (
                  <li key={file.id}>
                    <img
                      src={file.previewUrl}
                      alt={file.filename}
                      title={uiText("Enlarge image")}
                      onClick={() => setPreviewId(file.id)}
                    />
                    <button
                      type="button"
                      aria-label={uiText("Remove image")}
                      onClick={() => removeFile(file.id)}
                      disabled={busy}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="feedback-panel__hint">
            {uiText("The page URL, browser info, and recent errors are sent along with your feedback.")}
          </div>

          {error && <div className="feedback-panel__error">{error}</div>}

          <div className="feedback-panel__footer">
            <button
              type="button"
              className="feedback-panel__clear"
              onClick={resetDraft}
              disabled={busy}
            >
              {uiText("Clear")}
            </button>
            <button type="submit" className="feedback-panel__submit" disabled={busy}>
              {submitting ? uiText("Sending...") : uiText("Send")}
            </button>
          </div>
        </div>
      )}
    </form>
    {previewFile && (
      <div
        className="feedback-lightbox"
        role="dialog"
        aria-label={uiText("Enlarge image")}
        onClick={() => setPreviewId(null)}
        {...{ [FEEDBACK_WIDGET_ATTR]: '' }}
      >
        <img src={previewFile.previewUrl} alt={previewFile.filename} />
        <button
          type="button"
          className="feedback-lightbox__close"
          aria-label={uiText("Close")}
          onClick={() => setPreviewId(null)}
        >
          ×
        </button>
      </div>
    )}
    </>
  );
};

export default FeedbackPanelHost;
